import React, { memo, Fragment, useState, useRef, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import type { EditorActions } from '@atlaskit/editor-core';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import type { ADNode } from '@atlaskit/editor-common/validator';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Popup from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { SpotlightTarget } from '@atlaskit/onboarding';

import {
	LiveDocSpotlightsTour,
	LIVE_DOC_SHARE_BUTTON_TARGET,
} from '@confluence/onboarding-live-doc-changeboarding/entry-points/LiveDocSpotlightsTour';
import { useRouteActions } from '@confluence/route-manager';
import { useUnreadComments } from '@confluence/unread-comments';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { WebItemList } from '@confluence/web-item-list';
import { EDIT_PAGE_METADATA_RESTRICTIONS_ID } from '@confluence/web-item';
import {
	WebItemLocation,
	SYSTEM_EDITOR_PRECURSOR_BUTTONS,
	type WebItemLocationChildrenFnWithLoading,
} from '@confluence/web-item-location';
import { LazyFavouriteButton } from '@confluence/favourite-button';
import { FavouriteButtonPlaceholder } from '@confluence/favourite-button/entry-points/FavouriteButtonPlaceholder';
import { LazyDialogWrapperLoader, WATCH_DIALOG_LOCATION } from '@confluence/watch-dialog';
import { MANAGE_WATCHER_TAB_TYPES } from '@confluence/manage-watcher-dialog';
import { WatchButtonPlaceholder } from '@confluence/watch-dialog/entry-points/WatchButtonPlaceholder';
import {
	RestrictionsButtonPlaceholder,
	RestrictionsDialogTriggerWrapper,
} from '@confluence/restrictions';
import { PublishButtonWithMoreOptions } from '@confluence/publish-modal';
import type { FlagsStateContainer } from '@confluence/flags';
import type { PublishProps } from '@confluence/editor-features/entry-points/types';
import { ShareButton, CopyLinkButtonLoader } from '@confluence/share';
import {
	ShareAndRestrictButton,
	type ShareAndRestrictButtonProps,
	type ContentSubType,
	type ContentType,
} from '@confluence/share-and-restrict-dialog';
import { CopyLinkButtonPlaceholder } from '@confluence/share/entry-points/CopyLinkButtonPlaceholder';
import { LIVE_PAGE_SUBTYPE } from '@confluence/live-pages-utils';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { openInlineCommentInEditor, getSortedMarkerRefs } from '@confluence/comments-util';
import { useInlineCommentQueryParams } from '@confluence/comment';
import { getPlacement, CommentIconLoadable } from '@confluence/comment-button';
import { inlineCommentGifContent } from '@confluence/inline-comments-common';
import { EmbeddedPublishButton } from '@confluence/embedded-publish-button';
import { AccessStatus, ConfluenceEdition, useSessionData } from '@confluence/session-data';
import { CommentButtonPlaceholder } from '@confluence/comment-button/entry-points/CommentButtonPlaceholder';
import { PageGroupedButtonsAutomationMenu } from '@confluence/automation-menu/entry-points/PageGroupedButtonsAutomationMenu';
import { AutomationMenuButtonPlaceholder } from '@confluence/automation-menu/entry-points/AutomationMenuButtonPlaceholder';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { useCommentsContentState, useCommentsContentActions } from '@confluence/comment-context';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { fg } from '@confluence/feature-gating';
import { useAnnotations } from '@confluence/annotation-provider-store';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useShowCommentsPanel } from '@confluence/comments-panel';
import { usePublicLinkPageDataProvider } from '@confluence/public-links';
import { LivePagesModeChanger } from '@confluence/live-pages-features/entry-points/LivePagesModeChanger';
import { useUnifiedShareDialogEligible } from '@confluence/experiment-unified-share-dialog';

import { MainButtonContainer, SubsequentNodeWrapper } from './MainButtonStyles';
import { PreloadButtonWrapper } from './PreloadButtonWrapper';

const i18n = defineMessages({
	publishExternalShareLabel: {
		id: 'editor-main-buttons.editor.publish.external.share.label',
		defaultMessage: 'Externally shared',
		description: 'Text for external shared',
	},
	publishButtonDisableByEmptyTitleTooltip: {
		id: 'editor-main-buttons.editor.buttons.disable.by.empty.title.tooltip',
		defaultMessage: 'Your page needs a title before it can be published.',
		description: 'Tooltip text displayed when publish button is disabled due to empty title',
	},
	publishButtonDisableByNotSavingTooltip: {
		id: 'editor-main-buttons.editor.buttons.disable.by.not.saving.tooltip',
		defaultMessage: 'Still working...',
		description: 'Tooltip text displayed when publish button is disabled due to not saving',
	},
	publishButtonDisableBySavingTooltip: {
		id: 'editor-main-buttons.editor.buttons.disable.by.saving.tooltip',
		defaultMessage: 'We’re still saving your page.',
		description: 'Tooltip text displayed when publish button is disabled due to in saving',
	},
	closeButtonText: {
		id: 'editor-main-buttons.close.name',
		defaultMessage: 'Close',
		description: 'Text for close button',
	},
	closeDraftButtonText: {
		id: 'editor-main-buttons.close.draft.name',
		defaultMessage: 'Close draft',
		description: 'Text for close draft button',
	},
	discardDraftButtonText: {
		id: 'editor-main-buttons.discard.draft.name',
		defaultMessage: 'Discard draft',
		description: 'Text for discard draft button',
	},
	closeButtonPublishTooltip: {
		id: 'editor-main-buttons.editor.close.publish.tooltip',
		defaultMessage: 'Saves changes without publishing',
		description: 'Tooltip text displayed for close button when publishing',
	},
	closeButtonUpdateTooltip: {
		id: 'editor-main-buttons.editor.close.update.tooltip',
		defaultMessage: 'Saves changes without updating',
		description: 'Tooltip text displayed for close button when updating',
	},
	discardOnCloseButtonTooltip: {
		id: 'editor-main-buttons.editor.discard.close.tooltip',
		defaultMessage: 'Discard empty draft',
		description:
			"Tooltip text displayed for close button if it's an empty draft and will get discarded",
	},
	editScheduleTooltip: {
		id: 'editor-main-buttons.edit.schedule.tooltip',
		defaultMessage: 'Edit schedule',
		description: 'Inform user to edit the scheduled publish onclick of the scheduled button',
	},
	emptyBlogPostTitleDefault: {
		id: 'editor-main-buttons.empty.blogPostTitle',
		defaultMessage: 'Untitled blog post',
		description:
			'Default title for untitled blog post drafts. Please match editor.fabric.share.invite.to.edit.empty.blog.post.title in all languages',
	},
	emptyPageTitleDefault: {
		id: 'editor-main-buttons.empty.default-pageTitle',
		defaultMessage: 'Untitled page',
		description:
			'Default title for untitled page drafts. Please match editor-main-buttons.empty.default-pageTitle in all languages',
	},
	emptyLivePageTitleDefault: {
		id: 'editor-main-buttons.empty.default-livePageTitle',
		defaultMessage: 'Untitled live doc',
		description:
			'Default title for untitled live page drafts. Please match editor-main-buttons.empty.default-livePageTitle in all languages',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WebItemWrapper = styled.span({
	marginRight: token('space.150'),
});

const verticalSeparator = xcss({
	borderLeft: `2px solid ${token('color.border')}`,
	display: 'inline-block',
	width: '1px',
	height: '31px',
	marginBlock: `0`,
	marginInline: 'space.075',
});
const ssrModeSwitcher = xcss({
	height: '32px',
});

type ComponentProps = {
	/**
	 * Are the main buttons (cancel, publish, meatballs) disabled?
	 */
	areMainButtonsDisabled?: boolean;
	/**
	 * Should the legacy "Publish" button be disabled ? (Only used in embedded editor.)
	 */
	isPublishDisabled: boolean;
	/**
	 * Should the Publish Modal's buttons be disabled (i.e. Update, Publish..., Scheduled) ?
	 * If not provided, isPublishDisabled will be used.
	 */
	isPublishDisabledForPublishModal?: boolean;
	/**
	 * Optional React node comes before main buttons.
	 */
	precursoryNode?: React.ReactNode;
	/**
	 * Optional React node comes after main buttons.
	 */
	subsequentNode?: React.ReactNode;
	/**
	 * A callback function for clicking Close button
	 */
	onClose: () => void;
	/**
	 * A callback function for clicking Publish button
	 */
	onClickPublish: (arg: PublishProps) => () => void;
	/**
	 * A callback function for clicking the Preview button (used in publish modal)
	 */
	onClickPreview: () => void;
	/**
	 * Flag which identifies whether the title has content
	 */
	publishButtonDisabledReason: string;
	/**
	 * whether to allow web items (restriction)
	 */
	allowWebItems: boolean;
	/**
	 * whether to allow share button
	 */
	hideShare?: boolean;
	/**
	 * content id of a page/blog post
	 */
	contentId: string;
	/**
	 * Describes whether the content is a page or blogpost etc.
	 */
	contentType: string;
	/**
	 * Key of the space this content resides in
	 */
	spaceKey: string;
	/**
	 * Content is a draft
	 */
	isUnpublishedDraft: boolean;
	/**
	 * Indicates whether a publish is currently in progress
	 */
	isPublishingContent: boolean;
	/**
	 * Whether the close button and its tooltip will show that the draft is being discarded (true if draft is blank)
	 */
	discardOnCloseEnabled?: boolean;
	/**
	 * Function used within the publish modal to handle page to blog conversions.
	 */
	retrieveEditorTitleAndContent: () => Promise<{
		title?: string | null | undefined;
		adf: ADNode;
		syncRev?: string;
		ncsStepVersion?: string;
	}>;
	/**
	 * Indicates whether the editor is in preview mode. If it is, then the publish button/modal should not be displayed.
	 */
	shouldRenderPreview: boolean;
	/**
	 * Flags for use within the publish modal to handle any blog conversion errors.
	 */
	flags: FlagsStateContainer;
	/**
	 * Function used for saving the editor content to confluence backend
	 */
	saveDraft: () => void;
	/**
	 * Boolean to determine whether to render embedded publish button or publish button with more options
	 */
	useEmbeddedPublishButton?: boolean;

	/**
	 * Boolean to determine whether to render embedded publish button with the publish modal or the normal publish button with more options
	 */
	useEmbeddedPublishModal?: boolean;

	// To be cleaned up
	isPublishModalEnabled?: boolean;

	/**
	 * Page's title
	 */
	title: string | null;
	allowInlineComments?: boolean;
	allowStar?: boolean;
	allowWatch?: boolean;
	allowCopyLink?: boolean;
	allowRestrictions?: boolean;
	editorActions?: EditorActions;
	viewMode?: boolean;
	allowModeChanger?: boolean;
};

const publishButtonDisableReasonToTranslation = {
	'editor.buttons.disable.by.saving.tooltip': 'publishButtonDisableBySavingTooltip',
	'editor.buttons.disable.by.not.saving.tooltip': 'publishButtonDisableByNotSavingTooltip',
	'editor.buttons.disable.by.empty.title.tooltip': 'publishButtonDisableByEmptyTitleTooltip',
};

export const MainButtons: React.FC<ComponentProps> = memo(
	({
		areMainButtonsDisabled,
		subsequentNode,
		publishButtonDisabledReason,
		precursoryNode,
		isPublishDisabled,
		isPublishDisabledForPublishModal,
		onClickPublish,
		onClose,
		onClickPreview,
		allowWebItems,
		hideShare,
		contentId,
		spaceKey,
		isUnpublishedDraft: isUnpublishedDraftProp,
		isPublishingContent,
		discardOnCloseEnabled,
		retrieveEditorTitleAndContent,
		shouldRenderPreview,
		flags,
		saveDraft,
		useEmbeddedPublishButton,
		useEmbeddedPublishModal,
		contentType,
		title,
		allowInlineComments = true,
		allowStar = false,
		allowWatch = false,
		allowCopyLink = true,
		allowRestrictions = true,
		allowModeChanger = false,
		viewMode,
	}) => {
		const isLivePage = useIsCurrentPageLive();
		const { isUSDExperimentEnabled } = useUnifiedShareDialogEligible();

		const { showModal } = useDialogs();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const { editorCreateProps } = useAnnotations();
		const { hasContentChanged } = useCommentsContentState();
		const { resetContentChanged } = useCommentsContentActions();

		const { removeCommentQueryParams } = useInlineCommentQueryParams();
		const { edition, accessStatus } = useSessionData();

		const [isDialogOpen, setDialogState] = useState(false);
		const commentButtonRef = useRef<HTMLDivElement | null>(null);
		const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

		// When publishing an unpublished draft, the editor receives the PubSub event from the publish event
		// before the publish animation completes and closes the editor.
		// This causes distracting changes in the MainButtons that we want to stop.
		// To prevent them, we only allow isUnpublishedDraft to be updated when isPublishingContent is false.
		const [isUnpublishedDraft, setIsUnpublishedDraft] = useState(isUnpublishedDraftProp);
		useEffect(() => {
			if (!isPublishingContent) {
				setIsUnpublishedDraft(isUnpublishedDraftProp);
			}
		}, [isPublishingContent, isUnpublishedDraftProp]);
		const [{ unreadCommentsListState }] = useUnreadComments();

		const { isCommentsPanelShown, hideCommentsPanel, showCommentsPanel } = useShowCommentsPanel();
		const { setQueryParams } = useRouteActions();

		const publicLinkData = usePublicLinkPageDataProvider({
			pageId: contentId,
			fetchPolicy: 'cache-and-network',
		});
		const isPageExternallyShared =
			!publicLinkData.isLoading && publicLinkData.isPublicLinkFullyEnabled;
		const shouldShowWorldIcon =
			isPageExternallyShared &&
			!useEmbeddedPublishButton &&
			!(fg('confluence_frontend_unified_restrict_and_share') || isUSDExperimentEnabled);

		const intl = useIntl();
		const emptyPageTitleDefault =
			isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in')
				? i18n.emptyLivePageTitleDefault
				: i18n.emptyPageTitleDefault;
		const emptyDefaultTitle =
			contentType === 'blogpost' ? i18n.emptyBlogPostTitleDefault : emptyPageTitleDefault;

		const publishButtonDisabledTooltip = publishButtonDisabledReason
			? intl.formatMessage(
					i18n[publishButtonDisableReasonToTranslation[publishButtonDisabledReason]],
				)
			: '';
		const getPublishButton = () => {
			if (useEmbeddedPublishButton && !(useEmbeddedPublishModal && isUnpublishedDraft)) {
				return (
					<EmbeddedPublishButton
						onClickPublish={onClickPublish}
						publishButtonDisabled={isPublishDisabled || areMainButtonsDisabled}
						savingInProgress={isPublishingContent}
						isUnpublishedDraft={isUnpublishedDraft}
					/>
				);
			}

			const shouldShowFooterButtons = !(
				useEmbeddedPublishButton &&
				useEmbeddedPublishModal &&
				isUnpublishedDraft
			);

			return (
				<PublishButtonWithMoreOptions
					onClickPublish={onClickPublish}
					publishButtonDisabled={isPublishDisabledForPublishModal ?? isPublishDisabled}
					title={title}
					retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
					userViewingPreview={shouldRenderPreview}
					onClickPreview={onClickPreview}
					isUnpublishedDraft={isUnpublishedDraft}
					flags={flags}
					saveDraft={saveDraft}
					contentType={contentType}
					savingInProgress={!!isPublishingContent}
					shouldShowWorldIcon={shouldShowWorldIcon}
					shouldShowFooterButtons={shouldShowFooterButtons}
				/>
			);
		};

		const getCloseButton = () => {
			let closeBtn = (
				<Button
					id="close-button"
					data-test-id="close-button"
					appearance="subtle"
					onClick={onClose}
					isDisabled={areMainButtonsDisabled}
				>
					{discardOnCloseEnabled
						? intl.formatMessage(i18n.discardDraftButtonText)
						: intl.formatMessage(
								!useEmbeddedPublishButton && isUnpublishedDraft
									? i18n.closeDraftButtonText
									: i18n.closeButtonText,
							)}
				</Button>
			);

			// if the buttons are enabled, we should just show the normal tooltip of "close"
			if (!areMainButtonsDisabled) {
				closeBtn = (
					<AkTooltip
						content={
							discardOnCloseEnabled
								? intl.formatMessage(i18n.discardOnCloseButtonTooltip)
								: intl.formatMessage(
										isUnpublishedDraft
											? i18n.closeButtonPublishTooltip
											: i18n.closeButtonUpdateTooltip,
									)
						}
					>
						{closeBtn}
					</AkTooltip>
				);
			}

			return closeBtn;
		};

		const copyLinkButtonSource: { source: ShareAndRestrictButtonProps['source'] } = {
			source: 'editToolbar',
		};

		const getShareButton = ({ contentSubType }: { contentSubType: ContentSubType }) => (
			<>
				{fg('confluence_frontend_unified_restrict_and_share') || isUSDExperimentEnabled ? (
					<ShareAndRestrictButton
						contentId={contentId}
						contentType={contentType as ContentType}
						contentSubType={contentSubType}
						shareeAction="view"
						allowRestrictions={allowRestrictions}
						{...copyLinkButtonSource}
					/>
				) : (
					<ShareButton
						contentId={contentId}
						contentSubType={contentSubType || undefined}
						shareeAction="view"
						shareTitle={title || intl.formatMessage(emptyDefaultTitle)}
					/>
				)}
			</>
		);

		const checkUnsavedAndHandleCommentButtonClick = () => {
			// With the way the editor annotation provider works, it will show BOTH the the view components
			// at the same time, so if the create component is shown, ignore any changes and just trigger
			// the button press to select thefirst (or first unread) comment and let the closing of the create
			// flow handle the content changed checks and reset the changed values, this will only handle view
			if (!editorCreateProps && hasContentChanged) {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						handleCommentIconClickInEditor();
					},
				});
			} else {
				handleCommentIconClickInEditor();
			}
		};

		const handleCommentIconClickInEditor = () => {
			if (fg('confluence-frontend-comments-panel')) {
				if (isCommentsPanelShown) {
					hideCommentsPanel();
				} else {
					showCommentsPanel();
				}
				return;
			}

			const firstComment = document.querySelector(
				'.ak-editor-annotation-focus,.ak-editor-annotation-blur,.ak-editor-block-annotation-focus,.ak-editor-block-annotation-blur',
			);

			if (unreadCommentsListState?.length) {
				const unreadMarkerRefsListState = unreadCommentsListState
					?.filter((unreadComment) => Boolean(unreadComment.threadKey))
					.map((unreadComment) => unreadComment.threadKey as string);
				const sortedUnreadMarkerRefList = getSortedMarkerRefs(unreadMarkerRefsListState, 'edit');
				const unreadCommentMarkerRef = sortedUnreadMarkerRefList[0];

				const firstUnreadComment = document.getElementById(unreadCommentMarkerRef);
				openInlineCommentInEditor(firstUnreadComment as HTMLElement, unreadCommentMarkerRef);
				setQueryParams({
					unreadCommentMark: unreadCommentMarkerRef,
					focusedCommentId: undefined,
				});
			} else {
				if (firstComment) {
					setDialogState(false);

					const firstCommentID =
						firstComment?.closest('[data-mark-annotation-type="inlineComment"]')?.id ?? '';

					openInlineCommentInEditor(firstComment as HTMLElement, firstCommentID);
				} else {
					setDialogState(!isDialogOpen);
				}
				removeCommentQueryParams();
			}

			if (createAnalyticsEvent) {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'navigationItem',
						actionSubjectId: 'inlineCommentsIcon',
						attributes: {
							pageHasInlineComments: !!firstComment,
							icontype: 'header',
							context: 'default',
							newState: 'showComments',
							unreadCommentCount: unreadCommentsListState?.length,
						},
						objectType: 'page',
						objectId: contentId,
						source: 'editPageScreen',
					},
				}).fire();
			}
		};

		const getCommentIcon = (triggerProps: TriggerProps) => {
			return (
				<CommentIconLoadable
					triggerProps={triggerProps}
					intl={intl}
					handleCommentButtonClick={
						fg('confluence_frontend_editor_annotation_manager')
							? checkUnsavedAndHandleCommentButtonClick
							: handleCommentIconClickInEditor
					}
					commentButtonRef={commentButtonRef}
					isDisabled={areMainButtonsDisabled}
					contentId={contentId}
					pageMode="edit"
				/>
			);
		};

		/**
		 * Show automation when all conditions are true:
		 * 1. Live page
		 * 2. PREMIUM edition or 'a4c_free_and_standard_editions' === true
		 * 3. Is non-external collaborator
		 */
		const shouldShowAutomation =
			!fg('hide_confluence_manual_trigger_menu') &&
			isLivePage &&
			(edition === ConfluenceEdition.PREMIUM || fg('a4c_free_and_standard_editions')) &&
			accessStatus !== AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;

		const webItems: React.ReactElement[] = [];

		if (allowWebItems) {
			webItems.push(
				<WebItemWrapper data-test-id="editor-precursor-buttons-wrapper" key={webItems.length}>
					<WebItemLocation
						key="precursor-buttons"
						tagName="span"
						contentId={contentId}
						location={SYSTEM_EDITOR_PRECURSOR_BUTTONS}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={{ display: 'flex' }}
						renderWhenLoading
					>
						{
							// By default, there should be one web item, the restrictions web item. Additional web items may exist on tenants with Connect apps.
							(({ loading, webItems: loadedWebItems }) => {
								// If we're a live page and there are no web items (e.g. live view), we still always want to show the default buttons.
								if (loading || (!loadedWebItems.length && !isLivePage)) {
									return (
										<Fragment>
											{allowModeChanger && (
												<Box xcss={ssrModeSwitcher}>
													<LivePagesModeChanger contentId={contentId} isDisabled flags={flags} />
												</Box>
											)}
											<CommentButtonPlaceholder />
											{shouldShowAutomation && <AutomationMenuButtonPlaceholder />}
											{allowStar && <FavouriteButtonPlaceholder />}
											{allowWatch && <WatchButtonPlaceholder />}
											{!(
												fg('confluence_frontend_unified_restrict_and_share') ||
												isUSDExperimentEnabled
											) && <RestrictionsButtonPlaceholder />}
											{!(
												fg('confluence_frontend_unified_restrict_and_share') ||
												isUSDExperimentEnabled
											) && <CopyLinkButtonPlaceholder />}
										</Fragment>
									);
								}

								// The restrictions button is rendered via <RestrictionsDialogTriggerWrapper> instead of as a web item, to fix positioning issue CCP-871.
								// If in view mode (live view), do not render any web items as they are intended for users editing the page.
								const filteredWebItems =
									loading || viewMode
										? []
										: loadedWebItems.filter(({ id }) => id !== EDIT_PAGE_METADATA_RESTRICTIONS_ID);

								return (
									<Fragment>
										{allowModeChanger && (
											<LivePagesModeChanger
												contentId={contentId}
												isDisabled={!!areMainButtonsDisabled}
												flags={flags}
												analyticsSource="livePageScreen"
											/>
										)}

										{allowInlineComments && (
											<ErrorBoundary attribution={Attribution.COLLABORATION}>
												<Popup
													isOpen={isDialogOpen}
													content={inlineCommentGifContent}
													trigger={(triggerProps) => getCommentIcon(triggerProps)}
													placement={getPlacement(commentButtonRef.current)}
													onClose={() => {
														setDialogState(false);
													}}
													zIndex={layers.flag()}
													shouldRenderToParent
												/>
											</ErrorBoundary>
										)}
										{shouldShowAutomation && (
											<PageGroupedButtonsAutomationMenu
												key={`automation-manual-triggers-${contentId}`}
												pageId={contentId}
												spaceKey={spaceKey}
											/>
										)}
										{allowStar && (
											<LazyFavouriteButton
												key="favourite-button"
												contentId={contentId}
												isDisabled={areMainButtonsDisabled}
											/>
										)}
										{allowWatch && (
											<LazyDialogWrapperLoader
												key="watch-dialog"
												contentId={contentId}
												defaultTab={MANAGE_WATCHER_TAB_TYPES.PAGE}
												componentLocation={WATCH_DIALOG_LOCATION.PAGE}
												isDisabled={areMainButtonsDisabled}
											/>
										)}
										{allowRestrictions &&
											!(
												fg('confluence_frontend_unified_restrict_and_share') ||
												isUSDExperimentEnabled
											) && (
												<ErrorBoundary
													attribution={Attribution.PERMISSIONS_EXPERIENCE}
													key="restrictions-dialog-editor-trigger"
												>
													<SpotlightTarget name="live-pages-changeboarding.restrictions">
														<RestrictionsDialogTriggerWrapper
															contentId={contentId}
															triggeredFrom="fromEdit"
															isDisabled={areMainButtonsDisabled}
														/>
													</SpotlightTarget>
												</ErrorBoundary>
											)}
										{allowCopyLink &&
											!(
												fg('confluence_frontend_unified_restrict_and_share') ||
												isUSDExperimentEnabled
											) && (
												<CopyLinkButtonLoader
													contentId={contentId}
													contentType={contentType}
													pageMode="edit"
													source="editToolbar"
													spaceKey={spaceKey}
												/>
											)}
										{/* Other add-ons */}
										{filteredWebItems.length &&
										(!isLivePage || !fg('confluence_frontend_extension_points_in_live_pages')) ? (
											<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
												<WebItemList
													id="editor-precursor-buttons"
													dataTestId="editor-metadata-web-items"
													webItems={filteredWebItems}
												/>
											</ErrorBoundary>
										) : null}
									</Fragment>
								);
							}) as WebItemLocationChildrenFnWithLoading
						}
					</WebItemLocation>
				</WebItemWrapper>,
			);
		}

		// Live pages render share button instead of publish and close buttons
		if (isLivePage) {
			const shareButton = getShareButton({ contentSubType: LIVE_PAGE_SUBTYPE });

			const livePagesButtonGroup = (
				<SpotlightTarget name={LIVE_DOC_SHARE_BUTTON_TARGET}>
					<ButtonGroup>{shareButton}</ButtonGroup>
				</SpotlightTarget>
			);

			return (
				<MainButtonContainer data-vc="editor-main-buttons-container" {...ssrPlaceholderIdProp}>
					{webItems}
					{precursoryNode || null}
					<SpotlightTarget name="live-pages-changeboarding.share-button">
						{livePagesButtonGroup}
					</SpotlightTarget>
					{subsequentNode ? (
						<SubsequentNodeWrapper data-test-id="subsequent-node-wrapper">
							{subsequentNode}
						</SubsequentNodeWrapper>
					) : null}
					<LiveDocSpotlightsTour />
				</MainButtonContainer>
			);
		} else {
			const publishButton = getPublishButton();
			const closeButton = getCloseButton();

			const publishButtonGroup = (
				<ButtonGroup>
					{publishButton}
					{closeButton}
				</ButtonGroup>
			);

			const preloadedPublishButton = (
				<ButtonGroup>
					<PreloadButtonWrapper
						isPublish
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						isUnpublishedDraft={isUnpublishedDraft}
					>
						{publishButton}
					</PreloadButtonWrapper>
					<PreloadButtonWrapper
						isPublish={false}
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						isUnpublishedDraft={isUnpublishedDraft}
					>
						{closeButton}
					</PreloadButtonWrapper>
				</ButtonGroup>
			);

			return (
				<MainButtonContainer data-vc="editor-main-buttons-container" {...ssrPlaceholderIdProp}>
					{webItems}
					{precursoryNode || null}
					{areMainButtonsDisabled ? (
						<AkTooltip content={publishButtonDisabledTooltip} position="bottom">
							{publishButtonGroup}
						</AkTooltip>
					) : (
						<>{preloadedPublishButton}</>
					)}
					{accessStatus !== AccessStatus.EXTERNAL_COLLABORATOR_ACCESS &&
						!hideShare &&
						(fg('confluence_frontend_unified_restrict_and_share') || isUSDExperimentEnabled) && (
							<>
								<Box xcss={verticalSeparator} />
								<ShareAndRestrictButton
									contentId={contentId}
									contentType={contentType as ContentType}
									shareeAction="edit"
									allowRestrictions={allowRestrictions}
									isUnpublishedDraft={isUnpublishedDraft}
									{...copyLinkButtonSource}
								/>
							</>
						)}
					{subsequentNode ? (
						<SubsequentNodeWrapper data-test-id="subsequent-node-wrapper">
							{subsequentNode}
						</SubsequentNodeWrapper>
					) : null}
				</MainButtonContainer>
			);
		}
	},
);
